/**
 * WhatsappDraftDialog.jsx — Feb 2026
 *
 * Review + send AI-drafted WhatsApp follow-ups generated after an
 * AI-only voice call. Reviewer can edit, send-as-is, or dismiss.
 *
 * Props
 *   wsId           active workspace id
 *   job            voice_campaign_jobs row (must include id + whatsapp_draft)
 *   leadName       (optional) display name — falls back to job.lead.name
 *   leadPhone      (optional) display phone — falls back to job.lead.phone
 *   open           boolean controlled open state
 *   onClose(sent)  closes the dialog. `sent=true` when a send succeeded
 *                  so the caller can refresh its list.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Send, Save, Sparkles, Check } from "lucide-react";

export default function WhatsappDraftDialog({
  wsId, job, leadName, leadPhone, open, onClose,
}) {
  const [text, setText] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  // Track whether any save/send happened so the parent can refetch
  // its list even on a plain Close.
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open && job) {
      setText(job.whatsapp_draft || "");
      setDirty(false);
      setTouched(false);
    }
  }, [open, job]);

  if (!job) return null;

  const sent = !!job.whatsapp_sent_at;
  const name = leadName
    || job.lead?.name
    || job.contact?.name
    || job.manual_name
    || "lead";
  const phone = leadPhone
    || job.lead?.phone
    || job.contact?.phone
    || job.manual_phone
    || "—";

  const saveDraft = async () => {
    if (!text.trim()) { toast.error("Draft cannot be empty"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/workspaces/${wsId}/voice-campaigns/jobs/${job.id}/whatsapp-draft`,
        { text: text.trim() });
      toast.success("Draft saved");
      setDirty(false);
      setTouched(true);
      // Keep the local view consistent with what the server now holds
      // so re-opening the dialog or re-rendering the list card doesn't
      // flash the pre-edit text (the parent lifts this via onClose(true)
      // after send; this handles the save-and-stay-open path).
      if (data?.whatsapp_draft) setText(data.whatsapp_draft);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const sendDraft = async () => {
    if (!text.trim()) { toast.error("Draft cannot be empty"); return; }
    setSending(true);
    try {
      // Persist any pending edits before sending so the server copy
      // matches what the reviewer saw on screen.
      if (dirty) {
        await api.patch(
          `/workspaces/${wsId}/voice-campaigns/jobs/${job.id}/whatsapp-draft`,
          { text: text.trim() });
      }
      const { data } = await api.post(
        `/workspaces/${wsId}/voice-campaigns/jobs/${job.id}/send-whatsapp-draft`);
      if (data?.already_sent) {
        toast.info("Already sent earlier");
      } else {
        toast.success("WhatsApp follow-up sent");
      }
      onClose?.({ sent: true, touched: true });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Send failed"));
    } finally { setSending(false); }
  };

  const closeWith = (sent) => onClose?.({ sent: !!sent, touched });

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) closeWith(false); }}>
      <DialogContent className="rounded-none max-w-xl p-0"
                     data-testid="wa-draft-dialog">
        <DialogHeader className="px-5 pt-5 pb-2 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            WhatsApp follow-up draft
          </DialogTitle>
          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
            <span>To <span className="font-medium text-foreground">{name}</span></span>
            <span className="font-mono text-[11px]">{phone}</span>
            {job.whatsapp_draft_source === "ai" && (
              <Badge className="rounded-none border bg-emerald-50 text-emerald-700 border-emerald-500/40 text-[9px]">
                Drafted by AI
              </Badge>
            )}
            {job.whatsapp_draft_source === "template" && (
              <Badge className="rounded-none border bg-foreground/10 text-foreground/70 border-foreground/25 text-[9px]">
                Template fallback
              </Badge>
            )}
            {job.whatsapp_draft_source === "human_edited" && (
              <Badge className="rounded-none border bg-blue-50 text-blue-700 border-blue-500/40 text-[9px]">
                Edited
              </Badge>
            )}
            {job.whatsapp_draft_interest && (
              <Badge className="rounded-none border bg-amber-50 text-amber-700 border-amber-500/40 text-[9px]">
                Interested
              </Badge>
            )}
            {sent && (
              <Badge className="rounded-none border bg-emerald-50 text-emerald-700 border-emerald-500/40 text-[9px]">
                <Check className="w-2.5 h-2.5 mr-0.5" /> Sent
                {job.whatsapp_sent_by_name
                  ? ` by ${job.whatsapp_sent_by_name}`
                  : ""}
              </Badge>
            )}
          </div>
        </DialogHeader>

        <div className="p-5 space-y-2">
          <Textarea rows={9} value={text}
                    onChange={(e) => { setText(e.target.value); setDirty(true); }}
                    disabled={sent || sending}
                    className="rounded-none text-sm font-sans"
                    data-testid="wa-draft-textarea" />
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>{text.length} chars · WhatsApp session message</span>
            {dirty && !sent && <span className="text-amber-700">Unsaved changes</span>}
          </div>
        </div>

        <DialogFooter className="px-5 pb-5 flex-row justify-end gap-2">
          <Button variant="ghost" className="rounded-none"
                  onClick={() => closeWith(false)}
                  data-testid="wa-draft-cancel">
            Close
          </Button>
          {!sent && (
            <Button variant="outline" className="rounded-none"
                    onClick={saveDraft}
                    disabled={saving || sending || !dirty}
                    data-testid="wa-draft-save">
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {saving ? "Saving…" : "Save edits"}
            </Button>
          )}
          {!sent && (
            <Button className="rounded-none bg-emerald-600 hover:bg-emerald-700"
                    onClick={sendDraft}
                    disabled={sending || !text.trim()}
                    data-testid="wa-draft-send">
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {sending ? "Sending…" : "Send on WhatsApp"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
